import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../services/httpService';
import { API_CONSTANTS, API_HEADERS } from '../constants/api-constants';

// Types for rules
export interface MatchBlock {
  uuid?: string; // Add UUID for existing matchBlocks
  matchTexts: string[];
  found: boolean;
  source:
    | string
    | {
        uuid: string;
        code: string;
        description: string;
        label: string;
      };
  operator:
    | string
    | {
        uuid: string;
        code: string;
        description: string;
        label: string;
      }; // Can be string (legacy) or object (new format)
}

export interface Rule {
  uuid?: string;
  name: string;
  reason: string;
  action: string | null;
  enabled: boolean;
  version: string;
  markRuleForSpecificPayers: boolean;
  payerIds: string[];
  matchBlocks: MatchBlock[];
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateRuleRequest {
  name: string;
  reason: string;
  action: string | null;
  enabled: boolean;
  version: string;
  markRuleForSpecificPayers: boolean;
  payerIds: string[];
  matchBlocks: MatchBlock[];
}

export interface CreateRuleResponse {
  uuid: string;
  name: string;
  reason: string;
  action: string | null;
  enabled: boolean;
  version: string;
  createdAt: string;
  updatedAt: string;
  matchBlocks: MatchBlock[];
  markRuleForSpecificPayers: boolean;
  payers: Array<{
    uuid: string;
    name: string;
    athenaPayerId: string;
  }>;
}

export interface UpdateRuleRequest {
  name: string;
  reason: string;
  action: string | null;
  enabled: boolean;
  version: string;
  markRuleForSpecificPayers: boolean;
  payerIds: string[];
  matchBlocks: MatchBlock[];
}

// List response types
export interface RulesListModel {
  uuid: string;
  name: string;
  reason: string;
  action: string | null;
  enabled: boolean;
  version: string;
  createdAt: string;
  updatedAt: string;
  matchBlocks: Array<{
    id: number;
    matchTexts: string[];
    found: boolean;
    source: { uuid: string; sourceType: string };
  }>;
  markRuleForSpecificPayers: boolean;
  payers: Array<{ uuid: string; name: string; athenaPayerId: string }>;
}

export interface RulesListPayload {
  totalPages: number;
  totalElements: number;
  isFirst: boolean;
  isLast: boolean;
  models: RulesListModel[];
}

// Shared overview data shape (for each check type)
export interface OverviewStats {
  totalRules: number;
  activeRules: number;
  totalChecks: number;
  percentChange: number | null;
}

// Combined overview-by-check-type response
export interface OverviewByCheckTypeResponse {
  message: string;
  status: number;
  data: {
    RULE_CHECK: OverviewStats;
    PATIENT_HISTORY: OverviewStats;
    ENM_LEVEL: OverviewStats;
    ENM_AUTOCODER?: OverviewStats;
    SURGICAL_AUTOCODER?: OverviewStats;
  };
  timestamp: string;
}

export interface OverviewByCheckTypeData {
  RULE_CHECK: OverviewStats;
  PATIENT_HISTORY: OverviewStats;
  ENM_LEVEL: OverviewStats;
  ENM_AUTOCODER?: OverviewStats;
  SURGICAL_AUTOCODER?: OverviewStats;
  DENIAL_APPEAL?: OverviewStats;
}

// Async thunk for fetching combined overview by check type
export const fetchOverviewByCheckType = createAsyncThunk(
  'rules/fetchOverviewByCheckType',
  async (tenantId: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${API_CONSTANTS.RULES_BASE_URL}${API_CONSTANTS.ENDPOINTS.RULES.OVERVIEW_BY_CHECK_TYPE}`,
        {
          headers: {
            [API_HEADERS.TENANT_ID]: tenantId,
          },
        },
      );
      return response.data as OverviewByCheckTypeResponse;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to fetch overview by check type';
      return rejectWithValue(errorMessage);
    }
  },
);

// Async thunk for creating a rule
export const createRule = createAsyncThunk(
  'rules/createRule',
  async (
    { ruleData, tenantId }: { ruleData: CreateRuleRequest; tenantId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosInstance.post(
        `${API_CONSTANTS.RULES_BASE_URL}${API_CONSTANTS.ENDPOINTS.RULES.CREATE}`,
        ruleData,
        {
          headers: {
            'X-Tenant-ID': tenantId,
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      // Extract error message from API response
      let errorMessage = 'Failed to create rule';
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        errorMessage =
          axiosError.response?.data?.message ||
          axiosError.message ||
          errorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  },
);

// Async thunk: fetch list of rules
export const fetchRules = createAsyncThunk(
  'rules/fetchRules',
  async (
    {
      tenantId,
      ruleStatus,
      pageNo = 0,
      pageSize = 10,
      searchText,
    }: {
      tenantId: string;
      ruleStatus: 'ACTIVE' | 'INACTIVE';
      pageNo?: number;
      pageSize?: number;
      searchText?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      let url = `${API_CONSTANTS.RULES_BASE_URL}${API_CONSTANTS.ENDPOINTS.RULES.CREATE}?ruleStatus=${ruleStatus}&pageNo=${pageNo}&pageSize=${pageSize}`;

      if (searchText) {
        url += `&searchText=${encodeURIComponent(searchText)}`;
      }

      const response = await axiosInstance.get(url, {
        headers: { 'X-Tenant-ID': tenantId },
      });
      return response.data; // {message,status,data:{...}}
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch rules';
      return rejectWithValue(errorMessage);
    }
  },
);

// Async thunk: load more rules for pagination
export const loadMoreRules = createAsyncThunk(
  'rules/loadMoreRules',
  async (
    {
      tenantId,
      ruleStatus,
      pageNo,
      pageSize = 10,
      searchText,
    }: {
      tenantId: string;
      ruleStatus: 'ACTIVE' | 'INACTIVE';
      pageNo: number;
      pageSize?: number;
      searchText?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      let url = `${API_CONSTANTS.RULES_BASE_URL}${API_CONSTANTS.ENDPOINTS.RULES.CREATE}?ruleStatus=${ruleStatus}&pageNo=${pageNo}&pageSize=${pageSize}`;

      if (searchText) {
        url += `&searchText=${encodeURIComponent(searchText)}`;
      }

      const response = await axiosInstance.get(url, {
        headers: { 'X-Tenant-ID': tenantId },
      });
      return response.data; // {message,status,data:{...}}
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load more rules';
      return rejectWithValue(errorMessage);
    }
  },
);

// Async thunk: fetch a single rule by ID
export const fetchRuleById = createAsyncThunk(
  'rules/fetchRuleById',
  async (
    {
      ruleId,
      tenantId,
    }: {
      ruleId: string;
      tenantId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const url = `${API_CONSTANTS.RULES_BASE_URL}${API_CONSTANTS.ENDPOINTS.RULES.GET_BY_ID}/${ruleId}`;
      const response = await axiosInstance.get(url, {
        headers: { 'X-Tenant-ID': tenantId },
      });
      return response.data; // {message,status,data:{...}}
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch rule';
      return rejectWithValue(errorMessage);
    }
  },
);

// Async thunk: delete a rule by ID
export const deleteRule = createAsyncThunk(
  'rules/deleteRule',
  async (
    {
      ruleId,
      tenantId,
    }: {
      ruleId: string;
      tenantId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const url = `${API_CONSTANTS.RULES_BASE_URL}${API_CONSTANTS.ENDPOINTS.RULES.GET_BY_ID}/${ruleId}`;
      const response = await axiosInstance.delete(url, {
        headers: { 'X-Tenant-ID': tenantId },
      });
      return response.data; // {message,status,data:{...}}
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to delete rule';
      return rejectWithValue(errorMessage);
    }
  },
);

// Async thunk for updating a rule
export const updateRule = createAsyncThunk(
  'rules/updateRule',
  async (
    {
      ruleId,
      ruleData,
      tenantId,
    }: {
      ruleId: string;
      ruleData: UpdateRuleRequest;
      tenantId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosInstance.put(
        `${API_CONSTANTS.RULES_BASE_URL}${API_CONSTANTS.ENDPOINTS.RULES.UPDATE}/${ruleId}`,
        ruleData,
        {
          headers: {
            'X-Tenant-ID': tenantId,
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      // Extract error message from API response
      let errorMessage = 'Failed to update rule';
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        errorMessage =
          axiosError.response?.data?.message ||
          axiosError.message ||
          errorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  },
);

interface RulesState {
  rules: Rule[];
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  listLoading: boolean;
  listError: string | null;
  totalPages: number;
  totalElements: number;
  searchText: string; // Add searchText state
  // Pagination state
  currentPage: number;
  pageSize: number;
  isFirst: boolean;
  isLast: boolean;
  // Single rule state for editing
  singleRule: Rule | null;
  singleRuleLoading: boolean;
  singleRuleError: string | null;
  // Delete operation state
  deleteLoading: boolean;
  deleteError: string | null;
  // Overview state (by check type)
  overviewByCheckType: OverviewByCheckTypeData | null;
  overviewLoading: boolean;
  overviewError: string | null;
}

const initialState: RulesState = {
  rules: [],
  loading: false,
  error: null,
  successMessage: null,
  listLoading: false,
  listError: null,
  totalPages: 0,
  totalElements: 0,
  searchText: '', // Initialize searchText
  // Pagination state
  currentPage: 0,
  pageSize: 10,
  isFirst: true,
  isLast: true,
  // Single rule state for editing
  singleRule: null,
  singleRuleLoading: false,
  singleRuleError: null,
  // Delete operation state
  deleteLoading: false,
  deleteError: null,
  // Overview state
  overviewByCheckType: null,
  overviewLoading: false,
  overviewError: null,
};

const rulesSlice = createSlice({
  name: 'rules',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearSingleRule: (state) => {
      state.singleRule = null;
      state.singleRuleError = null;
    },
    clearSingleRuleError: (state) => {
      state.singleRuleError = null;
    },
    clearDeleteError: (state) => {
      state.deleteError = null;
    },
    setSearchText: (state, action) => {
      state.searchText = action.payload;
    },
    clearSearchText: (state) => {
      state.searchText = '';
    },
    resetPagination: (state) => {
      state.currentPage = 0;
      state.isFirst = true;
      state.isLast = true;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.currentPage = 0; // Reset to first page when changing page size
    },
  },
  extraReducers: (builder) => {
    // Create rule
    builder
      .addCase(createRule.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createRule.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = 'Rule created successfully';
        // Add the new rule to the list
        const newRule: Rule = {
          uuid: action.payload.data.uuid,
          name: action.payload.data.name,
          reason: action.payload.data.reason,
          action: action.payload.data.action,
          enabled: action.payload.data.enabled,
          version: action.payload.data.version,
          markRuleForSpecificPayers:
            action.payload.data.markRuleForSpecificPayers,
          payerIds: action.payload.data.payers.map(
            (p: { uuid: string; name: string; athenaPayerId: string }) =>
              p.athenaPayerId,
          ),
          matchBlocks: action.payload.data.matchBlocks,
          createdAt: action.payload.data.createdAt,
          updatedAt: action.payload.data.updatedAt,
        };
        state.rules.push(newRule);
      })
      .addCase(createRule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update rule
    builder
      .addCase(updateRule.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updateRule.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = 'Rule updated successfully';
        // Update the rule in the list
        const updatedRule: Rule = {
          uuid: action.payload.data.uuid,
          name: action.payload.data.name,
          reason: action.payload.data.reason,
          action: action.payload.data.action,
          enabled: action.payload.data.enabled,
          version: action.payload.data.version,
          markRuleForSpecificPayers:
            action.payload.data.markRuleForSpecificPayers,
          payerIds: action.payload.data.payers.map(
            (p: { uuid: string; name: string; athenaPayerId: string }) =>
              p.athenaPayerId,
          ),
          matchBlocks: action.payload.data.matchBlocks,
          createdAt: action.payload.data.createdAt,
          updatedAt: action.payload.data.updatedAt,
        };

        // Update in rules list
        const ruleIndex = state.rules.findIndex(
          (rule) => rule.uuid === updatedRule.uuid,
        );
        if (ruleIndex !== -1) {
          state.rules[ruleIndex] = updatedRule;
        }

        // Update single rule if it's the same one
        if (state.singleRule?.uuid === updatedRule.uuid) {
          state.singleRule = updatedRule;
        }
      })
      .addCase(updateRule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch list
    builder
      .addCase(fetchRules.pending, (state) => {
        state.listLoading = true;
        state.listError = null;
      })
      .addCase(fetchRules.fulfilled, (state, action) => {
        state.listLoading = false;
        const payload = action.payload.data as RulesListPayload;
        state.totalPages = payload.totalPages;
        state.totalElements = payload.totalElements;
        state.currentPage = 0; // Reset to first page for new search
        state.isFirst = payload.isFirst;
        state.isLast = payload.isLast;

        // Normalize into Rule[] shape for UI reuse
        state.rules = payload.models.map((m) => ({
          uuid: m.uuid,
          name: m.name,
          reason: m.reason,
          action: m.action,
          enabled: m.enabled,
          version: m.version,
          markRuleForSpecificPayers: m.markRuleForSpecificPayers,
          payerIds: m.payers.map((p) => p.athenaPayerId),
          matchBlocks: m.matchBlocks as unknown as MatchBlock[],
          createdAt: m.createdAt,
          updatedAt: m.updatedAt,
        }));
      })
      .addCase(fetchRules.rejected, (state, action) => {
        state.listLoading = false;
        state.listError = action.payload as string;
      });

    // Load more rules for pagination
    builder
      .addCase(loadMoreRules.pending, (state) => {
        // Don't set listLoading to true for pagination to avoid full screen loading
        state.listError = null;
      })
      .addCase(loadMoreRules.fulfilled, (state, action) => {
        const payload = action.payload.data as RulesListPayload;
        state.totalPages = payload.totalPages;
        state.totalElements = payload.totalElements;
        state.currentPage = payload.isFirst ? 0 : state.currentPage + 1;
        state.isFirst = payload.isFirst;
        state.isLast = payload.isLast;

        // Append new rules to existing ones
        const newRules = payload.models.map((m) => ({
          uuid: m.uuid,
          name: m.name,
          reason: m.reason,
          action: m.action,
          enabled: m.enabled,
          version: m.version,
          markRuleForSpecificPayers: m.markRuleForSpecificPayers,
          payerIds: m.payers.map((p) => p.athenaPayerId),
          matchBlocks: m.matchBlocks as unknown as MatchBlock[],
          createdAt: m.createdAt,
          updatedAt: m.updatedAt,
        }));

        state.rules = [...state.rules, ...newRules];
      })
      .addCase(loadMoreRules.rejected, (state, action) => {
        state.listError = action.payload as string;
      });

    // Fetch single rule by ID
    builder
      .addCase(fetchRuleById.pending, (state) => {
        state.singleRuleLoading = true;
        state.singleRuleError = null;
      })
      .addCase(fetchRuleById.fulfilled, (state, action) => {
        state.singleRuleLoading = false;
        const ruleData = action.payload.data;

        // Transform the API response to match our Rule interface
        const rule: Rule = {
          uuid: ruleData.uuid,
          name: ruleData.name,
          reason: ruleData.reason,
          action: ruleData.action,
          enabled: ruleData.enabled,
          version: ruleData.version,
          markRuleForSpecificPayers: ruleData.markRuleForSpecificPayers,
          payerIds:
            ruleData.payers?.map(
              (p: { uuid: string; name: string; athenaPayerId: string }) =>
                p.athenaPayerId,
            ) || [],
          matchBlocks: ruleData.matchBlocks || [],
          createdAt: ruleData.createdAt,
          updatedAt: ruleData.updatedAt,
        };

        state.singleRule = rule;
      })
      .addCase(fetchRuleById.rejected, (state, action) => {
        state.singleRuleLoading = false;
        state.singleRuleError = action.payload as string;
      });

    // Delete rule
    builder
      .addCase(deleteRule.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(deleteRule.fulfilled, (state, action) => {
        state.deleteLoading = false;
        // Remove the deleted rule from the rules list
        const deletedRuleId =
          action.payload.data?.ruleId || action.payload.data?.uuid;
        if (deletedRuleId) {
          state.rules = state.rules.filter(
            (rule) => rule.uuid !== deletedRuleId,
          );
        }
        // Clear single rule if it was the deleted one
        if (state.singleRule?.uuid === deletedRuleId) {
          state.singleRule = null;
        }
      })
      .addCase(deleteRule.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload as string;
      });

    // Fetch combined overview by check type
    builder
      .addCase(fetchOverviewByCheckType.pending, (state) => {
        state.overviewLoading = true;
        state.overviewError = null;
      })
      .addCase(fetchOverviewByCheckType.fulfilled, (state, action) => {
        state.overviewLoading = false;
        state.overviewByCheckType = action.payload.data;
      })
      .addCase(fetchOverviewByCheckType.rejected, (state, action) => {
        state.overviewLoading = false;
        state.overviewError = action.payload as string;
      });
  },
});

export const {
  clearError,
  clearSuccessMessage,
  clearSingleRule,
  clearSingleRuleError,
  clearDeleteError,
  setSearchText,
  clearSearchText,
  resetPagination,
  setPageSize,
} = rulesSlice.actions;

export default rulesSlice.reducer;
